/**
 * `timedEvent` rules (10 §3): things that happen at a stated time on the ground - the train
 * leaves, the convoy crosses, the search party arrives, the strike lands. Counted in ground
 * ticks (the mission clock can be shortened by modifiers; these cannot), brought forward once by
 * `hastenAtAlert`, and visible on the HUD from touchdown (src/ui/hud/missionRules.ts).
 *
 * They run whether or not the objective can still be had: the world goes on.
 */

import { type Ctx, isFighter } from '../ctx';
import { isAutomated } from '../enemies/specials/kinds';
import { EXPANSION, secs } from '../tuning';
import type { MissionDef, ObjectiveState, TimedState } from '../types';
import { dist, failObjective, inRect, loseCaptive } from './common';
import { captiveLocked } from './kinds';
import { fireTimed, type TimedRule, timedStillApplies } from './timedEffects';

export function createTimedStates(def: MissionDef): TimedState[] {
  const out: TimedState[] = [];
  def.objective.rules.forEach((rule, index) => {
    if (rule.type !== 'timedEvent') return;
    const waits = rule.from === 'armed' || rule.from === 'complete' || rule.from === 'zoneEntered';
    out.push({ rule: index, ticks: waits ? -1 : secs(rule.atSeconds), fired: false, hastened: false, windowTicks: 0 });
  });
  return out;
}

/** Has whatever this rule counts `from` happened yet? */
function started(o: ObjectiveState, rule: TimedRule): boolean {
  if (rule.from === 'armed') return o.armedTick >= 0;
  if (rule.from === 'complete') return o.completeTick >= 0;
  if (rule.from === 'zoneEntered') return o.zoneEnteredTick >= 0;
  return true;
}

/** The objective had until now (the event itself, or the end of its window): not done means lost. */
function judge(ctx: Ctx, rule: TimedRule): void {
  if (rule.failIfIncomplete !== true || ctx.world.objective.complete) return;
  failObjective(ctx, rule.reason ?? 'TOO LATE');
}

export function stepTimedEvents(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const o = w.objective;
  let open = false;
  // Events counted `from: 'zoneEntered'` start the first time he stands in the objective's zone, on his feet or not.
  const zone = statics.def.objective.zone;
  if (o.zoneEnteredTick < 0 && zone && inRect(zone, w.ranger.x, w.ranger.z)) o.zoneEnteredTick = o.groundTicks;
  for (const st of o.timed) {
    const rule = statics.def.objective.rules[st.rule];
    if (!rule || rule.type !== 'timedEvent') continue;
    if (st.fired) {
      if (st.windowTicks <= 0) continue;
      st.windowTicks--;
      if (st.windowTicks > 0) open = true;
      else {
        ctx.events.push({ type: 'timedEvent', effect: rule.effect, phase: 'windowClosed', rule: st.rule, seconds: 0 });
        judge(ctx, rule);
      }
      continue;
    }
    if (!timedStillApplies(ctx, rule)) {
      // Nothing left for it to do (every wagon already burning): spent without a word.
      st.fired = true;
      st.ticks = -1;
      continue;
    }
    if (st.ticks < 0) {
      if (!started(o, rule)) continue;
      st.ticks = secs(rule.atSeconds);
    }
    if (!st.hastened && rule.hastenAtAlert !== undefined && w.alert >= rule.hastenAtAlert) {
      st.hastened = true;
      st.ticks = Math.ceil(st.ticks * (rule.hastenFactor ?? 0.5));
      const seconds = Math.ceil(st.ticks / secs(1));
      ctx.events.push({ type: 'timedEvent', effect: rule.effect, phase: 'hastened', rule: st.rule, seconds });
    }
    if (st.ticks > 0) st.ticks--;
    if (st.ticks > 0) continue;
    st.fired = true;
    st.ticks = -1;
    st.windowTicks = secs(Math.max(0, rule.windowSeconds ?? 0));
    ctx.events.push({ type: 'timedEvent', effect: rule.effect, phase: 'fired', rule: st.rule, seconds: 0 });
    fireTimed(ctx, rule);
    if (st.windowTicks > 0) open = true;
    else judge(ctx, rule);
  }
  o.windowOpen = open;
}

/**
 * Once a search party is out, any soldier who comes on a man still in hiding has found him (mission 27:
 * "found first = lost"). People behind a lever, a pyramid or a prison wall are not in hiding, they are held.
 */
export function stepSearchParties(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const o = w.objective;
  let reason: string | undefined;
  let out = false;
  for (const st of o.timed) {
    const rule = statics.def.objective.rules[st.rule];
    if (!st.fired || rule?.type !== 'timedEvent' || rule.effect !== 'searchPartyArrives') continue;
    out = true;
    reason ??= rule.reason;
  }
  if (!out || w.tick % 10 !== 0) return;
  for (const c of o.captives) {
    if (c.state !== 'held' || captiveLocked(ctx, c.x, c.z)) continue;
    const finder = w.enemies.find((e) => isFighter(e) && e.state !== 'down' && !isAutomated(e.kind)
      && dist(e.x, e.z, c.x, c.z) <= EXPANSION.foundRadius);
    if (!finder) continue;
    loseCaptive(ctx, c);
    // With nobody left to bring out the mission is lost - and the player is told why, in the rule's own words.
    if (o.captives.every((other) => other.state === 'lost')) {
      failObjective(ctx, reason ?? 'THE SEARCH PARTY FOUND HIM FIRST');
    }
  }
}
